import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../components/inputs/Button";
import AlertBanner from "../components/shared/AlertBanner";
import Badge from "../components/shared/Badge";
import Card from "../components/shared/Card";
import EmptyState from "../components/shared/EmptyState";
import ProgressBar from "../components/shared/ProgressBar";
import Textarea from "../components/shared/Textarea";
import { useAuth } from "../hooks/useAuth";

const ShortlistReview = () => {
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const [shortlist, setShortlist] = useState(null);
  const [reviews, setReviews] = useState({});
  const [comments, setComments] = useState({});
  const [finalDecisions, setFinalDecisions] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    // Load shortlist from localStorage
    const savedShortlist = localStorage.getItem("panelShortlist");
    if (savedShortlist) {
      const parsed = JSON.parse(savedShortlist);
      setShortlist(parsed);

      // Load existing reviews if any
      const existingReviews = localStorage.getItem("shortlistReviews");
      if (existingReviews) {
        const parsedReviews = JSON.parse(existingReviews);
        setReviews(parsedReviews);

        // Check if current user has already submitted
        if (
          hasRole("COMMUNITY_MEMBER") &&
          parsedReviews.committeeReviews?.[user?.id]
        ) {
          setIsSubmitted(true);
        }
        if (
          hasRole("CLIENT_MEMBER") &&
          parsedReviews.clientReviews?.[user?.id]
        ) {
          setIsSubmitted(true);
        }
      }
    }
  }, [user, hasRole]);

  const handleReviewChange = (cadreId, decision) => {
    setReviews((prev) => ({
      ...prev,
      [cadreId]: decision,
    }));
  };

  const handleCommentChange = (cadreId, comment) => {
    setComments((prev) => ({
      ...prev,
      [cadreId]: comment,
    }));
  };

  const submitReview = () => {
    const reviewData = {
      reviewerId: user?.id,
      reviewerName: user?.name,
      reviewerRole: user?.role,
      reviews: reviews,
      comments: comments,
      submittedDate: new Date().toISOString(),
    };

    // Get existing reviews
    const existingReviews = JSON.parse(
      localStorage.getItem("shortlistReviews") || "{}"
    );

    // Add review based on role
    if (hasRole("COMMUNITY_MEMBER")) {
      if (!existingReviews.committeeReviews) {
        existingReviews.committeeReviews = {};
      }
      existingReviews.committeeReviews[user?.id] = reviewData;
    } else if (hasRole("CLIENT_MEMBER")) {
      if (!existingReviews.clientReviews) {
        existingReviews.clientReviews = {};
      }
      existingReviews.clientReviews[user?.id] = reviewData;
    }

    // Save to localStorage
    localStorage.setItem("shortlistReviews", JSON.stringify(existingReviews));

    // Simulate email notification
    console.log("Review submitted:", reviewData);
    console.log("Email notification sent to admin");

    alert("Your review has been submitted successfully!");
    setIsSubmitted(true);
  };

  const calculateConsensus = () => {
    const allReviews = JSON.parse(
      localStorage.getItem("shortlistReviews") || "{}"
    );
    const consensus = {};

    shortlist?.selectedCadre?.forEach((cadre) => {
      let approvals = 0;
      let rejections = 0;
      let alternates = 0;

      // Count committee reviews
      Object.values(allReviews.committeeReviews || {}).forEach((review) => {
        if (review.reviews[cadre.id] === "approve") approvals++;
        else if (review.reviews[cadre.id] === "reject") rejections++;
        else if (review.reviews[cadre.id] === "alternate") alternates++;
      });

      // Count client reviews
      Object.values(allReviews.clientReviews || {}).forEach((review) => {
        if (review.reviews[cadre.id] === "approve") approvals++;
        else if (review.reviews[cadre.id] === "reject") rejections++;
        else if (review.reviews[cadre.id] === "alternate") alternates++;
      });

      // Determine consensus
      if (approvals > rejections && approvals > alternates) {
        consensus[cadre.id] = "APPROVED";
      } else if (rejections > approvals) {
        consensus[cadre.id] = "REJECTED";
      } else {
        consensus[cadre.id] = "ALTERNATE";
      }
    });

    return consensus;
  };

  const finalizeSelection = () => {
    const consensus = calculateConsensus();
    const finalList = shortlist.selectedCadre.filter(
      (cadre) => consensus[cadre.id] === "APPROVED"
    );
    const alternates = shortlist.selectedCadre.filter(
      (cadre) => consensus[cadre.id] === "ALTERNATE"
    );

    const finalSelection = {
      panelId: shortlist.panelId,
      panelTitle: shortlist.panelTitle,
      approvedCadre: finalList,
      alternateCadre: alternates,
      finalizedDate: new Date().toISOString(),
      finalizedBy: user?.name,
      status: "FINALIZED",
    };

    // Save to localStorage
    localStorage.setItem("finalPanelSelection", JSON.stringify(finalSelection));

    // Assign Panel Admin (first approved member for demo)
    if (finalList.length > 0) {
      const panelAdmin = finalList[0];
      console.log(`Panel Admin assigned: ${panelAdmin.name}`);
      console.log("Email sent to Panel Admin with assignment details");
    }

    console.log("Final selection:", finalSelection);
    alert("Panel selection has been finalized! Panel Admin has been notified.");
    setFinalDecisions(consensus);
  };

  const getReviewProgress = () => {
    const allReviews = JSON.parse(
      localStorage.getItem("shortlistReviews") || "{}"
    );
    const committeeCount = Object.keys(
      allReviews.committeeReviews || {}
    ).length;
    const clientCount = Object.keys(allReviews.clientReviews || {}).length;

    return {
      committee: { completed: committeeCount, total: 3 },
      client: { completed: clientCount, total: 2 },
    };
  };

  if (!shortlist) {
    return (
      <div className="min-h-[calc(100vh-200px)] flex items-center justify-center">
        <EmptyState
          icon={
            <svg
              className="w-16 h-16 text-gray-400 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
          }
          title="No Shortlist Available"
          message="There are no panel shortlists awaiting your review at this time."
          action={
            <Button variant="primary" onClick={() => navigate("/dashboard")}>
              Return to Dashboard
            </Button>
          }
        />
      </div>
    );
  }

  const isCommittee = hasRole("COMMUNITY_MEMBER");
  const isClient = hasRole("CLIENT_MEMBER");
  const isAdmin = hasRole("PLATFORM_ADMIN");
  const canReview = (isCommittee || isClient) && !isSubmitted;
  const canFinalize = isAdmin;
  const progress = getReviewProgress();

  return (
    <div className="bg-gray-50 min-h-[calc(100vh-200px)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Shortlist Review</h1>
          <p className="text-gray-600 mt-2">
            {isCommittee &&
              "Review and provide feedback on the proposed panel members"}
            {isClient && "Review and approve the proposed panel members"}
            {isAdmin && "View review status and finalize panel selection"}
          </p>
        </div>

        {/* Panel Information */}
        <Card title="Panel Information" variant="elevated" className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-gray-600">Panel Title</p>
              <p className="font-medium text-gray-900">
                {shortlist.panelTitle}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Submitted By</p>
              <p className="font-medium text-gray-900">
                {shortlist.submittedBy}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Status</p>
              <Badge variant="warning">{shortlist.status}</Badge>
            </div>
          </div>
        </Card>

        {/* Review Status (Admin View) */}
        {isAdmin && (
          <Card title="Review Status" variant="elevated" className="mb-6">
            <div className="space-y-4">
              <div>
                <ProgressBar
                  value={progress.committee.completed}
                  max={progress.committee.total}
                  label="Committee Reviews"
                  variant="default"
                />
                <p className="text-sm text-gray-600 mt-1">
                  {progress.committee.completed} of {progress.committee.total}{" "}
                  completed
                </p>
              </div>
              <div>
                <ProgressBar
                  value={progress.client.completed}
                  max={progress.client.total}
                  label="Client Reviews"
                  variant="default"
                />
                <p className="text-sm text-gray-600 mt-1">
                  {progress.client.completed} of {progress.client.total}{" "}
                  completed
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Submitted Message */}
        {isSubmitted && !isAdmin && (
          <AlertBanner
            type="success"
            title="Review Submitted"
            message="Thank you for your review. You will be notified once the final selection is made."
            className="mb-6"
          />
        )}

        {/* Cadre Members List */}
        <Card title="Proposed Cadre Members" variant="elevated">
          <div className="space-y-4">
            {shortlist.selectedCadre?.map((cadre) => (
              <div
                key={cadre.id}
                className="border border-gray-200 rounded-lg p-4"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">
                      {cadre.name}
                    </h3>
                    <p className="text-sm text-gray-600">{cadre.email}</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {cadre.expertise?.map((exp) => (
                        <Badge key={exp} variant="default" size="sm">
                          {exp}
                        </Badge>
                      ))}
                    </div>
                    <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                      <span>{cadre.yearsExperience} years exp.</span>
                      <span>{cadre.region}</span>
                      <span className="flex items-center">
                        <svg
                          className="w-4 h-4 text-yellow-400 mr-1"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                        {cadre.rating}
                      </span>
                    </div>
                  </div>

                  {/* Review Actions */}
                  {canReview && (
                    <div className="ml-4">
                      <div className="flex flex-col space-y-2">
                        <Button
                          variant={
                            reviews[cadre.id] === "approve"
                              ? "primary"
                              : "outline"
                          }
                          size="sm"
                          onClick={() =>
                            handleReviewChange(cadre.id, "approve")
                          }
                          className={
                            reviews[cadre.id] === "approve"
                              ? "bg-emerald-800 hover:bg-emerald-900"
                              : ""
                          }
                        >
                          Approve
                        </Button>
                        <Button
                          variant={
                            reviews[cadre.id] === "reject"
                              ? "danger"
                              : "outline"
                          }
                          size="sm"
                          onClick={() => handleReviewChange(cadre.id, "reject")}
                        >
                          Reject
                        </Button>
                        <Button
                          variant={
                            reviews[cadre.id] === "alternate"
                              ? "primary"
                              : "outline"
                          }
                          size="sm"
                          onClick={() =>
                            handleReviewChange(cadre.id, "alternate")
                          }
                          className={
                            reviews[cadre.id] === "alternate"
                              ? "bg-yellow-600 hover:bg-yellow-700"
                              : ""
                          }
                        >
                          Alternate
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Final Decision Display (Admin) */}
                  {isAdmin && finalDecisions[cadre.id] && (
                    <div className="ml-4">
                      <Badge
                        variant={
                          finalDecisions[cadre.id] === "APPROVED"
                            ? "success"
                            : finalDecisions[cadre.id] === "REJECTED"
                            ? "danger"
                            : "warning"
                        }
                      >
                        {finalDecisions[cadre.id]}
                      </Badge>
                    </div>
                  )}
                </div>

                {/* Comments Section */}
                {canReview && reviews[cadre.id] && (
                  <div className="mt-4">
                    <Textarea
                      label="Comments (Optional)"
                      name={`comment-${cadre.id}`}
                      value={comments[cadre.id] || ""}
                      onChange={(e) =>
                        handleCommentChange(cadre.id, e.target.value)
                      }
                      rows={2}
                      placeholder="Add any comments or concerns..."
                    />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between mt-6 pt-6 border-t">
            <Button variant="outline" onClick={() => navigate("/dashboard")}>
              Back to Dashboard
            </Button>

            {canReview && (
              <Button
                variant="primary"
                onClick={submitReview}
                disabled={Object.keys(reviews).length === 0}
              >
                Submit Review
              </Button>
            )}

            {canFinalize && (
              <Button
                variant="primary"
                className="bg-emerald-800 hover:bg-emerald-900"
                onClick={finalizeSelection}
              >
                Finalize Selection
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ShortlistReview;
